import React from 'react';

// US state code → full name (mirror of backend state_full_name())
const STATE_CODE_TO_NAME = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',DC:'District of Columbia',FL:'Florida',GA:'Georgia',
  HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',
  LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',
  MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',
  NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',
  OH:'Ohio',OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',
  SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',
  WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',
};
const NAME_TO_CODE = Object.fromEntries(
  Object.entries(STATE_CODE_TO_NAME).map(([c, n]) => [n.toLowerCase(), c])
);

/**
 * EmailSignaturePreview — visual preview of the business-card footer
 * that the server (`email_inbox._build_signature_html`) appends to
 * EVERY outbound email. Render this at the bottom of any compose
 * surface so the user sees what their recipient will see.
 *
 * Source-of-truth lives in `EmailPage.js`; this file is the shared
 * extract so the override-flow ContactBottomSheet renders the EXACT
 * same card.
 */
export default function EmailSignaturePreview({ user, onOpenPreview }) {
  const name = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email || '';
  const role = (user?.role || '').trim();
  const rawState = (user?.state || '').trim();
  const upper = rawState.toUpperCase();
  const stateCode = STATE_CODE_TO_NAME[upper] ? upper : NAME_TO_CODE[rawState.toLowerCase()] || '';
  const stateFull = STATE_CODE_TO_NAME[stateCode] || (STATE_CODE_TO_NAME[upper] ? STATE_CODE_TO_NAME[upper] : rawState);
  const customTitle = user?.is_custom_role && (user?.title || '').trim() ? user.title.trim() : null;
  const roleCap = role ? role.charAt(0).toUpperCase() + role.slice(1) : '';
  const title = customTitle || (stateFull && roleCap ? `${stateFull} ${roleCap}` : roleCap);
  const firstName = (name.split(' ')[0] || 'They').trim();
  const photoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/card/${user?.id || ''}/photo.jpg`;
  // Mirror backend: when the user hasn't uploaded a profile picture,
  // collapse the photo column down to 1/3 (40px) and render it as a
  // flat dark-grey strip with no <img>.
  const hasPhoto = !!(user?.profile_picture && String(user.profile_picture).trim());
  // iter134 — Hide the "X is a community member…" footer for
  // supervisors / admins / special users (created_by_admin truthy).
  const roleLower = (user?.role || '').toLowerCase().trim();
  const isSpecial = !!user?.created_by_admin;
  const hideTagline = isSpecial || ['supervisor', 'admin', 'super_admin'].includes(roleLower);
  // iter133 — Phone-mode mirrors backend `_build_signature_html`.
  // When an ORB phone is assigned: name in normal case, no accent
  // rule, title+company stacked tight, phone row below.
  const rawPhone = (user?.orb_phone || '').trim();
  const hasPhone = !!rawPhone;
  let phoneDisplay = rawPhone;
  if (hasPhone) {
    let digits = rawPhone.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    if (digits.length === 10) phoneDisplay = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  const Dots = () => (
    <span className="inline-flex items-center gap-[4px]">
      <span className="w-[5px] h-[5px] rounded-full bg-[#9BAF92] inline-block" />
      <span className="w-[5px] h-[5px] rounded-full bg-[#9BAF92] inline-block" />
      <span className="w-[5px] h-[5px] rounded-full bg-[#9BAF92] inline-block" />
    </span>
  );

  return (
    <div
      className="mx-3 my-2 rounded-[14px] overflow-hidden bg-[#0f0f0f] shadow-md flex"
      data-testid="email-signature-preview"
      style={{ height: 160 }}
    >
      {hasPhoto ? (
        <div className="w-[120px] bg-[#1a1a1a] flex-shrink-0">
          <img
            src={photoUrl}
            alt=""
            className="block w-full h-full object-cover object-center"
            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
          />
        </div>
      ) : (
        <div className="w-[40px] bg-[#1a1a1a] flex-shrink-0" aria-hidden="true" />
      )}
      <div className="flex-1 px-4 py-3 text-white flex flex-col min-w-0">
        <div className="mb-1.5"><Dots /></div>
        {hasPhone ? (
          <>
            <p className="font-black text-white leading-tight tracking-tight truncate"
               style={{ fontSize: 16 }}>
              {name}
            </p>
            {title && (
              <p className="text-[10px] font-medium text-[#9BAF92] leading-tight truncate mt-0.5">{title}</p>
            )}
            <p className="text-[9px] text-[#9BAF92] tracking-[0.08em] mt-px font-semibold">
              ORB | Collective Solutions
            </p>
            <p className="text-[9px] text-[#9BAF92] tracking-[0.06em] mt-px mb-1.5 font-semibold" data-testid="sig-phone">
              {phoneDisplay}
            </p>
          </>
        ) : (
          <>
            <p className="font-black uppercase text-white leading-none tracking-tight truncate"
               style={{ fontSize: 16 }}>
              {name}
            </p>
            <div className="w-7 h-[2px] bg-[#9BAF92] mt-2 mb-1.5" />
            {title && (
              <p className="text-[10px] font-medium text-[#9BAF92] leading-tight truncate">{title}</p>
            )}
            <p className="text-[9px] text-[#9BAF92] tracking-[0.08em] mt-0.5 mb-2 font-semibold">
              ORB | Collective Solutions
            </p>
          </>
        )}
        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={onOpenPreview}
            className="bg-[#586D52] hover:bg-[#4a5d46] text-white rounded-full px-2.5 py-1 text-[9px] font-bold tracking-[0.1em] uppercase transition-colors text-center"
            data-testid="email-sig-preview-btn"
          >
            ▶ Learn about ORB
          </button>
          <span
            className="bg-[#9BAF92] text-[#0f0f0f] rounded-full px-2.5 py-1 text-[9px] font-bold tracking-[0.1em] uppercase text-center"
            data-testid="email-sig-careers-btn"
          >
            ✦ Join our Team
          </span>
        </div>
        {!hideTagline && (
          <div className="mt-auto pt-1.5 flex items-end gap-1.5">
            <p className="text-[8px] text-[#888] leading-tight flex-1 truncate">
              {firstName} is a community member using ORB Uplift.
            </p>
            <Dots />
          </div>
        )}
      </div>
    </div>
  );
}
